import { useEffect, useMemo, useRef, useState } from "react";

export interface SearchableOption {
  value: string;
  label: string;
  hint?: string;
}

/**
 * Filterable select. Drop-in replacement for a native <select> when the
 * option list grows long enough that scrolling becomes annoying. Uses a
 * text input + a filtered dropdown below; arrow-keys / Enter / Escape work,
 * click-outside closes.
 */
export function SearchableSelect({
  value,
  options,
  placeholder = "— choose —",
  disabled = false,
  required = false,
  onChange,
  emptyHint,
}: {
  value: string;
  options: SearchableOption[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  onChange: (value: string) => void;
  emptyHint?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // The text the user sees when nothing is in the search field — the
  // currently-selected option's label.
  const selected = options.find((o) => o.value === value);
  const display = open ? query : (selected?.label ?? "");

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter((o) =>
      o.label.toLowerCase().includes(q) ||
      (o.hint?.toLowerCase().includes(q) ?? false),
    );
  }, [query, options]);

  // Close on click-outside.
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    }
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function pick(opt: SearchableOption) {
    onChange(opt.value);
    setOpen(false);
    setQuery("");
    inputRef.current?.blur();
  }

  function onKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = filtered[highlight];
      if (opt) pick(opt);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setQuery("");
      inputRef.current?.blur();
    }
  }

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%" }}>
      <input
        ref={inputRef}
        type="text"
        value={display}
        onFocus={() => { setOpen(true); setHighlight(0); }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(0); }}
        onKeyDown={onKey}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        spellCheck={false}
        autoComplete="off"
        style={{ width: "100%", paddingRight: 28 }}
      />
      {/* Caret */}
      <span
        aria-hidden
        style={{
          position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
          color: "var(--muted)", fontSize: 10, pointerEvents: "none",
        }}
      >▼</span>

      {/* Clear button (shown when something is selected and field has focus on tab) */}
      {!disabled && value && !open && (
        <button
          type="button"
          aria-label="Clear"
          onClick={() => onChange("")}
          style={{
            position: "absolute", right: 24, top: "50%", transform: "translateY(-50%)",
            background: "transparent", border: "none", color: "var(--muted)",
            fontSize: 14, cursor: "pointer", padding: "0 4px",
          }}
        >×</button>
      )}

      {open && (
        <div
          role="listbox"
          style={{
            position: "absolute", top: "100%", left: 0, right: 0,
            zIndex: 100, marginTop: 2,
            maxHeight: 280, overflowY: "auto",
            background: "var(--panel)", border: "1px solid var(--border)",
            borderRadius: 6, boxShadow: "0 4px 12px rgba(15,23,42,0.08)",
          }}
        >
          {filtered.length === 0 && (
            <div className="muted" style={{ padding: "8px 10px", fontSize: 13 }}>
              {emptyHint ?? "No matches."}
            </div>
          )}
          {filtered.map((opt, i) => {
            const active = i === highlight;
            const sel = opt.value === value;
            return (
              <div
                key={opt.value}
                role="option"
                aria-selected={sel}
                onMouseDown={(e) => { e.preventDefault(); pick(opt); }}
                onMouseEnter={() => setHighlight(i)}
                style={{
                  padding: "6px 10px",
                  background: active ? "var(--accent-soft)" : "transparent",
                  cursor: "pointer",
                  fontSize: 13,
                  display: "flex", flexDirection: "column", gap: 2,
                  borderLeft: sel ? "3px solid var(--accent)" : "3px solid transparent",
                }}
              >
                <div style={{ fontWeight: sel ? 600 : 400, color: "var(--text)" }}>{opt.label}</div>
                {opt.hint && (
                  <div className="muted" style={{ fontSize: 11 }}>{opt.hint}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
